/* pantallas-alumno (B): LA VENTANA H DEL PANEL CON LO QUE PIDEN LAS PANTALLAS DEL ALUMNO.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Es la ventana de la seccion H del Panel (`components/panel/VentanaDelPanel.js`, intocable): mismo
   velo (`FONDO_DE_LA_VENTANA`, negro al 66 %, fijo sobre TODA la pantalla, tambien la barra de arriba),
   misma caja (#161616, borde #2A2A2A, radio 20; en el celular hoja desde abajo con radio 24 arriba y
   agarradera), mismos botones (`BOTON_VENTANA_*`: 48 a lo ancho en el celular con el principal ARRIBA,
   40 desde 768) y la misma conducta (portal al `body`, Esc y el toque en el velo cierran, el foco
   vuelve a `volverA`, se desplaza por dentro y los botones quedan fijos abajo).

   Lo que la del Panel no trae y los documentos del alumno piden, y por eso existe esta pieza:
   - `ancho`: 440 (por omision), 480 (temas, documento grande, paquetes), 560 (cancelar la
     suscripcion) o 600 (editar flashcard).
   - `etiquetaCerrar`: la X, SOLO en las ventanas que la tienen hoy (40, 44 de toque en el celular).
   - `cerrarConFondo={false}`: la que no se cierra al tocar fuera (por ejemplo, una invitacion).
   Sin esas tres props pinta lo mismo que la del Panel.

   smoke-alumno, S2: VUELVE LA ANIMACION, con los tiempos de la casa en 46df225. En el telefono la hoja sube desde
   abajo en 500 ms y baja en 300 ms, suave (los de la hoja de abajo de `ui/sheet.jsx`); desde 768 la ventana se
   desvanece y crece de 95 % en 200 ms (los del dialogo de `ui/dialog.jsx`). El velo se desvanece con ella. Con
   "reducir movimiento", nada. Quien abre la ventana la DESMONTA al cerrarla (cualquier boton, Esc, el velo, cambiar
   de ruta), asi que la salida la pinta una COPIA MUDA de la ventana: sin testids ni rol, inerte y sin toques, que
   se quita sola al terminar. Asi ningun sitio de llamada cambia y el cierre sigue siendo inmediato para el codigo.
   Sin `matchMedia` o con "reducir movimiento", no hay copia.

   tutor-3 (C.6, P11), abierta SOLO para esto y sin cambiar nada de su aspecto (`arrastreDeHoja.js`):
   - En el telefono, ARRASTRAR la agarradera hacia abajo mueve la hoja con el dedo; soltar con mas de 80 bajados, o
     con un tiron rapido, la cierra (`alCerrar`); si no, regresa. El blanco es la FILA de la agarradera, un area
     invisible de 44 de alto a todo lo ancho; la "x" (si la hay) queda encima de esa area.
   - El FRENO del "jalar para refrescar" de Safari: `overscroll-behavior: contain` en el cuerpo que se desplaza y en el
     velo, `touch-action: pan-y` en el cuerpo, y mientras la hoja esta abierta `overscroll-behavior: none` en `html`.
   Desde 768 (la ventana centrada) nada de esto aplica. */
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { FONDO_DE_LA_VENTANA } from '../panel/VentanaDelPanel';
import { AreaDe44 } from '../panel/anatomia';
import {
  useArrastreDeHoja, useFrenoDelRefresco, ENTRA_EL_VELO, ENTRA_LA_CAJA, pintarLaSalida,
} from './arrastreDeHoja';

export {
  BOTON_VENTANA, BOTON_VENTANA_PRINCIPAL, BOTON_VENTANA_SECUNDARIO, BOTON_VENTANA_BORRAR, FONDO_DE_LA_VENTANA,
} from '../panel/VentanaDelPanel';

const ANCHOS = {
  440: 'md:max-w-[440px]',
  480: 'md:max-w-[480px]',
  560: 'md:max-w-[560px]',
  600: 'md:max-w-[600px]',
};
const CAJA = 'w-full max-h-[90dvh] flex flex-col bg-[#161616] border border-[#2A2A2A] rounded-t-[24px] md:rounded-[20px] shadow-2xl';

// S2: la entrada y la salida, y la copia muda que pinta la salida: desde marca-1 (#726) viven en `arrastreDeHoja.js`,
// para todas las hojas de la casa (mismas cadenas, mismos tiempos).

export default function Ventana({
  titulo, children, botones, alCerrar, volverA, testid, etiquetaId, ancho = 440, etiquetaCerrar, cerrarConFondo = true,
}) {
  const cerrarRef = useRef(alCerrar);
  cerrarRef.current = alCerrar;
  const volverRef = useRef(volverA);
  const veloRef = useRef(null);
  const cajaRef = useRef(null);
  // S2: al desmontarse (se cerro por donde sea), la salida.
  useLayoutEffect(() => {
    const velo = veloRef.current;
    const caja = cajaRef.current;
    return () => pintarLaSalida(velo, caja);
  }, []);
  // tutor-3 (C.6): el arrastre de la agarradera y el freno del refresco (solo en el telefono)
  const agarradera = useArrastreDeHoja(cajaRef, alCerrar);
  useFrenoDelRefresco(true, true, veloRef);
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape' && cerrarRef.current) cerrarRef.current(); };
    document.addEventListener('keydown', tecla);
    const volver = volverRef.current;
    return () => {
      document.removeEventListener('keydown', tecla);
      const b = volver && volver.current;
      if (b && typeof b.focus === 'function') b.focus();
    };
  }, []);

  return createPortal(
    <div ref={veloRef} className={`${FONDO_DE_LA_VENTANA} ${ENTRA_EL_VELO}`}
      onClick={(e) => { e.stopPropagation(); if (cerrarConFondo && e.target === e.currentTarget && alCerrar) alCerrar(); }}
      data-testid={testid ? `${testid}-fondo` : undefined}>
      <div ref={cajaRef} role="dialog" aria-modal="true" aria-labelledby={etiquetaId}
        className={`${CAJA} ${ANCHOS[ancho] || ANCHOS[440]} ${ENTRA_LA_CAJA}`} onClick={(e) => e.stopPropagation()} data-testid={testid} data-ancho={ancho}>
        <div className="md:hidden relative flex justify-center pt-2.5" aria-hidden="true">
          <span className="w-10 h-1 rounded-full bg-[#3A3A3A]" />
          <span className="absolute inset-x-0 top-0 h-11 z-[1]" {...agarradera} data-testid={testid ? `${testid}-agarradera` : undefined} />
        </div>
        <div className="relative flex-1 min-h-0 overflow-y-auto px-6 pt-4 md:pt-6 max-md:overscroll-contain max-md:touch-pan-y" data-ventana-cuerpo="">
          {etiquetaCerrar ? (
            <button type="button" onClick={alCerrar} aria-label={etiquetaCerrar} title={etiquetaCerrar}
              className="absolute z-[2] top-2.5 right-3 md:top-4 md:right-4 w-10 h-10 rounded-xl flex items-center justify-center text-[#9A9A9A] hoverable:text-white hoverable:bg-[#1B1B1B] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              data-testid={testid ? `${testid}-cerrar` : undefined}>
              <AreaDe44 />
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          ) : null}
          <h2 id={etiquetaId} className={`text-[17px] font-semibold text-[#F4F4F3] break-words${etiquetaCerrar ? ' pr-10' : ''}`}>{titulo}</h2>
          <div className="mt-3">{children}</div>
        </div>
        {botones ? (
          <div className="shrink-0 px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:pb-6 flex flex-col-reverse md:flex-row md:justify-end gap-2">
            {botones}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
